/* React Imports */
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router';

/* Styling Imports */
import './pricing.scss';
import {
  Row,
  Col,
  Button,
  Card,
  List,
  Tabs,
  Typography,
  message,
  Skeleton,
  Tag,
} from 'antd';
import { ListIcon } from 'assets/icons';
const { Paragraph, Title } = Typography;

/* API Services */
import AuthService from 'api-services/auth-service';
import { getAllPlans, subscribe } from 'api-services/pricing';
import {
  BUSINESS_PLAN_LOOKUP,
  FREE_PLAN_LOOKUP,
  SUBSCRIPTION_STATUS,
} from 'utilities/constants';

/* Hooks */
import useLoading from 'hooks/use-loading';
import useDecodeToken from 'hooks/use-decode-token';
import Helper from 'utilities/helper';
import eventEmitter from 'utilities/event-emitter';

function Pricing() {
  const { state } = useLocation();
  const navigate = useNavigate();

  /* Custom Hooks */
  const { setLoading } = useLoading();
  const { subscriptionId, subscriptionStatus, planId, userId } =
    useDecodeToken();

  /* Component States */
  const [plans, setPlans] = useState([]);
  const [skeleton, setSkeleton] = useState(true);
  const getButtonLabel = (lbl) =>
    userId === null
      ? 'Sign up'
      : subscriptionStatus === SUBSCRIPTION_STATUS.CONSUMED
        ? 'Renew Plan'
        : lbl;

  /* Handling purchase */
  const handlePurchase = async (plan) => {
    if (userId === null) {
      navigate('/register');
      return;
    }

    setLoading(true);
    const response = await subscribe(userId, plan.uid); //subscribing to plan
    if (response.status === 201) {
      //positive response handling
      if (response.data.url) window.location.replace(response.data.url);
      //redirecting to stripe incase of paid plan
      else {
        const token = await AuthService.refreshToken(); //refreshing token with updated values
        eventEmitter.emit('userInfoUpdated', token);

        message.success(
          'Thank you for choosing Chat Buddy Pro. You have been subscribed to free plan.'
        );

        navigate('/chatbots');
      }
    }
    setLoading(false);
  };

  /* Use Effects */
  useEffect(() => {
    const getPlans = async () => {
      setSkeleton(true);
      const response = await getAllPlans(); //getting all available plans
      if (response.status === 200) {
        //positive response handling
        let tempPlans = response.data;
        if (state?.upgrade) {
          tempPlans = tempPlans.filter((p) => {
            return p.lookupKey !== FREE_PLAN_LOOKUP;
          });
        }
        setPlans(tempPlans); //setting plans in state
      }
      setSkeleton(false);
    };

    if (plans.length === 0) {
      //initial call to get all plans
      getPlans();
    }
  }, [setPlans, subscriptionId, planId]);

  /* UI */
  return (
    <>
      <Row className="pricing-plans">
        <Col span={24}>
          <h1 className="page-title">Pricing Plans</h1>
          <Paragraph>Get 2 months for free by subscribing yearly!</Paragraph>
          <Tabs
            defaultActiveKey="1"
            items={[
              {
                label: 'Monthly',
                key: '1',
                children: (
                  <Row gutter={[28, 28]} justify="center">
                    {skeleton && (
                      <>
                        <PlanSkeleton />
                        <PlanSkeleton />
                        <PlanSkeleton />
                      </>
                    )}
                    {!skeleton && (
                      <>
                        {(planId === null || planId === '') && (
                          <PaymentPlan
                            showButton={
                              planId === FREE_PLAN_LOOKUP || planId === ''
                            }
                            key={FREE_PLAN_LOOKUP}
                            plan={{
                              uid: '0fd3d80e',
                              lookupKey: FREE_PLAN_LOOKUP,
                              name: 'Free',
                              rate: '$0',
                            }}
                            btnLabel={getButtonLabel('Get Free')}
                            btnOnClick={handlePurchase}
                          />
                        )}
                        {plans.map((val) => {
                          if (val.planTerm === 'MONTHLY')
                            return (
                              <PaymentPlan
                                showButton={
                                  planId === FREE_PLAN_LOOKUP || planId === ''
                                }
                                key={val.lookupKey}
                                plan={val}
                                btnLabel={getButtonLabel('Purchase')}
                                btnOnClick={handlePurchase}
                                currentPlan={planId}
                              />
                            );
                        })}
                        <PaymentPlan
                          showButton={
                            true
                          }
                          key={BUSINESS_PLAN_LOOKUP + '_monthly'}
                          plan={{
                            lookupKey: BUSINESS_PLAN_LOOKUP,
                            name: 'Business',
                            rate: 'Contact Us',
                          }}
                          btnLabel="Contact Us"
                          btnOnClick={() => {
                            window.location.href = '/contact.html';
                          }}
                        />
                      </>
                    )}
                  </Row>
                ),
              },
              {
                label: 'Yearly',
                key: '2',
                children: (
                  <Row gutter={[28, 28]} justify="center">
                    {skeleton && (
                      <>
                        <PlanSkeleton />
                        <PlanSkeleton />
                        <PlanSkeleton />
                      </>
                    )}
                    {!skeleton && (
                      <>
                        {plans.map((val) => {
                          if (
                            val.planTerm === 'ANNUAL' &&
                            val.lookupKey !== planId
                          )
                            return (
                              <PaymentPlan
                                showButton={
                                  planId === FREE_PLAN_LOOKUP || planId === ''
                                }
                                key={val.lookupKey}
                                plan={val}
                                btnLabel={getButtonLabel('Purchase')}
                                btnOnClick={handlePurchase}
                                currentPlan={planId}
                              />
                            );
                        })}
                        <PaymentPlan
                          showButton={true}
                          key={BUSINESS_PLAN_LOOKUP + '_annual'}
                          plan={{
                            lookupKey: BUSINESS_PLAN_LOOKUP,
                            name: 'Business',
                            rate: 'Contact Us',
                          }}
                          btnLabel="Contact Us"
                          btnOnClick={() => {
                            window.location.href = '/contact.html';
                          }}
                        />
                      </>
                    )}
                  </Row>
                ),
              },
            ]}
          />
        </Col>
      </Row>
    </>
  );
}

function PaymentPlan({ plan, currentPlan, btnLabel, btnOnClick, showButton }) {
  /* eslint-disable indent */
  const getPlanRate = (plan) => {
    switch (plan.planTerm) {
      case 'ANNUAL':
        return `$${plan.rate}/Year`;
      case 'MONTHLY':
        return `$${plan.rate}/Month`;
      default:
        return '';
    }
  };

  return (
    <Col
      xs={24}
      sm={12}
      md={12}
      lg={8}
      xl={6}
      className="d-flex align-items-stretch plans-wrp"
    >
      <Card key={plan.lookupKey} className="pricing-card">
        <Title level={2}>
          {plan.name}{' '}
          {plan.lookupKey === currentPlan && (
            <Tag
              bordered={false}
              color="purple"
              size="small"
              style={{ marginLeft: '5px' }}
            >
              Current
            </Tag>
          )}
        </Title>
        <div className="list-wrap">
          <List
            dataSource={Helper.getPlanFeatures(plan.lookupKey)}
            renderItem={(item) => (
              <List.Item>
                <ListIcon />
                {item}
              </List.Item>
            )}
          />
        </div>
        <div className='p-bottom'>
        <Title level={4} className="pricing">
          {getPlanRate(plan)}
        </Title>
        {showButton && (
          <Button onClick={() => btnOnClick(plan)} type="primary">
            {currentPlan === FREE_PLAN_LOOKUP ? 'Upgrade' : btnLabel}
          </Button>
        )}
        </div>
      </Card>
    </Col>
  );
}

function PlanSkeleton() {
  return (
    <>
      <Col xs={8} sm={8} md={8} lg={8} xl={8}>
        <Card>
          <Skeleton active />
        </Card>
      </Col>
    </>
  );
}

export default Pricing;
